import { ReactNode } from 'react';

import { Chip, Box, Typography, SxProps, Theme } from '@mui/material';

import { AnimatedCollapse } from './AnimatedCollapse';
import { BadgeCircle } from './BadgeCircle';

type BadgeProps = Readonly<{
  children: ReactNode;
  badgeName: string;
  title: string;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  loading?: boolean;
}>;

export const BadgeGroup = ({
  children,
  badgeName,
  title,
  isOpen,
  setIsOpen,
  loading = false,
}: BadgeProps) => {
  return (
    <Box sx={containerSx(isOpen)}>
      <Box sx={badgeContainerSx} onClick={() => setIsOpen(!isOpen)}>
        <Chip
          variant="badge"
          label={badgeName}
          size="md"
          icon={<BadgeCircle loading={loading} size="md" />}
          sx={chipSx(loading)}
          type="leading"
        />
        <Typography variant="text-sm-medium" sx={titleSx}>
          {title}
        </Typography>
      </Box>
      <Box sx={collapseContainerSx}>
        <AnimatedCollapse isOpen={isOpen} springFactor={1.2} key={badgeName}>
          {children}
        </AnimatedCollapse>
      </Box>
    </Box>
  );
};

const containerSx =
  (isOpen: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    cursor: 'pointer',
    alignItems: 'flex-start',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: backrs.colors['Colors/Base/White'],
    minHeight: isOpen ? '56px' : 'auto',
    height: { xs: 'fit-content', md: 'max-content' },
    borderRadius: isOpen ? backrs.radius['radius-md'] : backrs.radius['radius-xl'],
    borderTopLeftRadius: `${backrs.radius[isOpen ? 'radius-none' : 'radius-xl']}`,
    border: `1px solid ${backrs.colors['Gray/800']}`,
    '&:hover': {
      boxShadow: backrs.shadows['shadow-md'],
      transform: 'translate(-2px, -1px)',
    },
    transition: 'all 0.3s ease-in-out',
    flex: 'none',
    overflow: 'hidden',
    paddingRight: backrs.spacing['spacing-xs'],
  });

const titleSx: SxProps<Theme> = {
  paddingY: '3px', // 3px to get the lines vertically centered with the chip when the title wraps (or doesn't wrap)
};

const badgeContainerSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  userSelect: 'none',
  top: 0,
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  alignItems: 'flex-start',
  borderRadius: backrs.spacing['spacing-md'],
  padding: backrs.spacing['spacing-xs'],
  gap: backrs.spacing['spacing-md'],
  transition: 'padding 0.3s ease-in-out',
});

const chipSx =
  (loading: boolean): SxProps<Theme> =>
  ({ backrs }) => ({
    '& .MuiChip-icon': {
      marginLeft: backrs.spacing['spacing-none'],
      marginRight: backrs.spacing['spacing-none'],
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    '& .MuiChip-label': {
      marginLeft: loading ? '-8px' : undefined,
    },
  });

const collapseContainerSx: SxProps<Theme> = {
  minHeight: 0,
};
